import React, { useState, useEffect } from 'react';
import { reservationApi, fairManagerApi } from '../services/api';
import { Reservation, ReservationAnalytics, Fair } from '../types';
import {
  CheckCircle,
  XCircle,
  Calendar,
  User,
  MapPin,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  TrendingUp,
  DollarSign,
  Users,
  Percent
} from 'lucide-react';
import { showSuccess, showError, showWarning } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency } from '../utils/currency';

interface FairManagerReservationsProps {
  fairId?: number; // Optional: if provided, loads that fair's reservations directly
}

const FairManagerReservations: React.FC<FairManagerReservationsProps> = ({ fairId: initialFairId }) => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [analytics, setAnalytics] = useState<ReservationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showApproveModal, setShowApproveModal] = useState<number | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<number | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [exportingCSV, setExportingCSV] = useState(false);

  // Fair selection
  const [managedFairs, setManagedFairs] = useState<Fair[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<number | null>(initialFairId || null);
  const [loadingFairs, setLoadingFairs] = useState(true);

  const pageSize = 20;

  // Load managed fairs on component mount
  useEffect(() => {
    loadManagedFairs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load reservations when fair or filters change
  useEffect(() => {
    if (selectedFairId) {
      loadReservations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFairId, statusFilter, page]);

  // Keyboard navigation - Escape key closes modals
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showApproveModal) {
          setShowApproveModal(null);
          setAdminNotes('');
        }
        if (showRejectModal) {
          setShowRejectModal(null);
          setAdminNotes('');
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showApproveModal, showRejectModal]);

  const loadManagedFairs = async () => {
    try {
      setLoadingFairs(true);
      // Get list of managed fairs using Fair Manager API
      const fairs = await fairManagerApi.getFairs();
      setManagedFairs(fairs);

      // If no fair selected and there are fairs, select the first one
      if (!selectedFairId && fairs.length > 0) {
        setSelectedFairId(fairs[0].id);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load managed fairs:', apiError);
      setError(apiError.message);
    } finally {
      setLoadingFairs(false);
    }
  };

  const loadReservations = async () => {
    if (!selectedFairId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await reservationApi.getFairReservations(
        selectedFairId,
        statusFilter === 'all' ? undefined : statusFilter,
        page,
        pageSize
      );

      setReservations(response.items);
      setTotal(response.total);
      setTotalPages(response.total_pages);
      setAnalytics(response.analytics || null); // Issue #378 - Set analytics data
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load reservations:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedFairId) return;

    try {
      setExportingCSV(true);
      await reservationApi.exportFairReservationsCSV(
        selectedFairId,
        statusFilter === 'all' ? undefined : statusFilter
      );
      showSuccess('CSV exported successfully', {
        description: 'The file has been downloaded to your device',
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to export CSV:', apiError);
      showError('Failed to export CSV', {
        description: apiError.message,
      });
    } finally {
      setExportingCSV(false);
    }
  };

  const handleApprove = async (reservationId: number) => {
    if (processingId) return;

    try {
      setProcessingId(reservationId);
      await reservationApi.updateReservationStatus(reservationId, {
        status: 'confirmed',
        admin_notes: adminNotes || undefined
      });
      setShowApproveModal(null);
      setAdminNotes('');
      await loadReservations();
      showSuccess('Reservation approved successfully', {
        description: 'The vendor has been notified',
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to approve reservation:', apiError);
      showError('Failed to approve reservation', {
        description: apiError.message,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reservationId: number) => {
    if (processingId) return;
    if (!adminNotes.trim()) {
      showWarning('Please provide a reason for rejection', {
        description: 'A reason is required when rejecting reservations',
      });
      return;
    }

    try {
      setProcessingId(reservationId);
      await reservationApi.updateReservationStatus(reservationId, {
        status: 'cancelled',
        admin_notes: adminNotes
      });
      setShowRejectModal(null);
      setAdminNotes('');
      await loadReservations();
      showSuccess('Reservation rejected successfully', {
        description: 'The vendor has been notified',
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to reject reservation:', apiError);
      showError('Failed to reject reservation', {
        description: apiError.message,
      });
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Confirmed' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Cancelled' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: 'bg-gray-100 text-gray-800',
      icon: AlertCircle,
      text: status
    };

    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loadingFairs) {
    return (
      <div className="flex justify-center items-center min-h-screen" data-testid="reservations-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (managedFairs.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center" data-testid="no-managed-fairs-message">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Managed Fairs</h3>
          <p className="text-gray-600">You don't have any fairs assigned to manage yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Reservations</h1>
        <p className="text-gray-600">Manage vendor booth reservations and track signups</p>
      </div>

      {/* Fair Selector */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label htmlFor="fair-selector" className="block text-sm font-medium text-gray-700 mb-2">
          Select Fair
        </label>
        <select
          id="fair-selector"
          data-testid="reservation-fair-select"
          value={selectedFairId || ''}
          onChange={(e) => {
            setSelectedFairId(Number(e.target.value));
            setPage(1); // Reset to first page when changing fair
          }}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
        >
          <option value="">Select a fair...</option>
          {managedFairs.map((fair) => (
            <option key={fair.id} value={fair.id}>
              {fair.name} - {fair.address.city}, {fair.address.state}
            </option>
          ))}
        </select>
      </div>

      {selectedFairId && (
        <>
          {/* Analytics Dashboard (Issue #378) */}
          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="reservation-analytics">
              {/* Total Confirmed Vendors Card */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6 text-white" data-testid="analytics-confirmed-vendors">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <Users className="w-6 h-6" />
                  </div>
                  <TrendingUp className="w-5 h-5 opacity-80" />
                </div>
                <div className="text-3xl font-bold mb-1">{analytics.total_confirmed}</div>
                <div className="text-teal-100 text-sm">Confirmed Vendors</div>
              </div>

              {/* Total Revenue Card */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white" data-testid="analytics-total-revenue">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <DollarSign className="w-6 h-6" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">{formatCurrency(analytics.total_revenue)}</div>
                <div className="text-green-100 text-sm">Total Revenue</div>
              </div>

              {/* Average Reservation Value Card */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6 text-white" data-testid="analytics-avg-value">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <DollarSign className="w-6 h-6" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">{formatCurrency(analytics.average_reservation_value)}</div>
                <div className="text-teal-100 text-sm">Avg Reservation Value</div>
              </div>

              {/* Occupancy Rate Card */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white" data-testid="analytics-occupancy-rate">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <Percent className="w-6 h-6" />
                  </div>
                </div>
                <div className="text-3xl font-bold mb-1">{analytics.occupancy_rate.toFixed(1)}%</div>
                <div className="text-purple-100 text-sm">Occupancy Rate</div>
              </div>
            </div>
          )}

          {/* Filters and Export */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
                  Status:
                </label>
                <select
                  id="status-filter"
                  data-testid="reservation-status-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1); // Reset to first page when changing filter
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* CSV Export Button (Issue #378) */}
              <button
                data-testid="export-csv-button"
                onClick={handleExportCSV}
                disabled={exportingCSV || reservations.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingCSV ? 'Exporting...' : 'Export Vendor List (CSV)'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="reservation-error-message">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12" data-testid="reservation-list-loading">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Reservations Table */}
          {!loading && reservations.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="reservation-list">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendor Business Name {/* Issue #378 - New column */}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Email {/* Issue #378 - New column */}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Slot
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reservation Date {/* Issue #378 - New column */}
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reservations.map((reservation) => (
                      <tr key={reservation.id} className="hover:bg-gray-50" data-testid="reservation-item">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {reservation.vendor_name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {reservation.vendor_email || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{reservation.slot_number}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{reservation.reservation_type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-500">{formatDate(reservation.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(reservation.price_paid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span data-testid="reservation-status-badge">{getStatusBadge(reservation.status)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {reservation.status === 'pending' && (
                            <div className="flex justify-end gap-2">
                              <button
                                data-testid="approve-reservation-button"
                                onClick={() => setShowApproveModal(reservation.id)}
                                disabled={processingId !== null}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                aria-label={`Approve reservation for ${reservation.vendor_name || 'vendor'}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approve
                              </button>
                              <button
                                data-testid="reject-reservation-button"
                                onClick={() => setShowRejectModal(reservation.id)}
                                disabled={processingId !== null}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                aria-label={`Reject reservation for ${reservation.vendor_name || 'vendor'}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-testid="reservation-pagination">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      data-testid="pagination-previous-mobile"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      data-testid="pagination-next-mobile"
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700" data-testid="pagination-info">
                        Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{' '}
                        <span className="font-medium">{total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          data-testid="pagination-previous-button"
                          onClick={() => setPage(page - 1)}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700" data-testid="pagination-current-page">
                          Page {page} of {totalPages}
                        </span>
                        <button
                          data-testid="pagination-next-button"
                          onClick={() => setPage(page + 1)}
                          disabled={page === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!loading && reservations.length === 0 && (
            <div className="bg-white rounded-lg shadow p-12 text-center" data-testid="no-reservations-message">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reservations Found</h3>
              <p className="text-gray-600">
                {statusFilter === 'all'
                  ? 'There are no reservations for this fair yet.'
                  : `There are no ${statusFilter} reservations for this fair.`}
              </p>
            </div>
          )}
        </>
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="approve-modal-title"
          data-testid="approve-modal"
        >
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full">
            <h3 id="approve-modal-title" className="text-lg font-semibold text-gray-900 mb-4">Approve Reservation</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to approve this reservation?
            </p>
            <div className="mb-4">
              <label htmlFor="approve-notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                id="approve-notes"
                data-testid="approve-notes-input"
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Add any notes for the vendor..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                data-testid="reservation-approve-cancel-button"
                onClick={() => {
                  setShowApproveModal(null);
                  setAdminNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                aria-label="Cancel approval"
              >
                Cancel
              </button>
              <button
                data-testid="reservation-approve-confirm-button"
                onClick={() => handleApprove(showApproveModal)}
                disabled={processingId !== null}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                aria-label="Confirm reservation approval"
              >
                {processingId ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reject-modal-title"
          data-testid="reject-modal"
        >
          <div className="relative bg-white rounded-lg shadow-xl p-6 m-4 max-w-md w-full">
            <h3 id="reject-modal-title" className="text-lg font-semibold text-gray-900 mb-4">Reject Reservation</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this reservation. This will be sent to the vendor.
            </p>
            <div className="mb-4">
              <label htmlFor="reject-notes" className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reject-notes"
                data-testid="reject-notes-input"
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Explain why this reservation is being rejected..."
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                data-testid="reservation-reject-cancel-button"
                onClick={() => {
                  setShowRejectModal(null);
                  setAdminNotes('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                aria-label="Cancel rejection"
              >
                Cancel
              </button>
              <button
                data-testid="reservation-reject-confirm-button"
                onClick={() => handleReject(showRejectModal)}
                disabled={processingId !== null || !adminNotes.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                aria-label="Confirm reservation rejection"
              >
                {processingId ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FairManagerReservations;
